import React from 'react'
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { Category } from 'venue/parkMap/models'
import { sortPoisByName } from '../../../helpers'
import CategoryLi, { CategoryLiOverlay } from './categoryListItem'
import PoiLi, { ExtendedPoi, PoiLiOverlay } from './poiListItem'

interface CategoriesPoisListProps {
    data: Category[]
    onDragEnd(event: DragEndEvent): void
    onEditCategory(categoryId: string): void
    onDeleteCategory(categoryId: string): void
    onDeletePoi(poiId: string): void
    onAddPoi(categoryId: string): void
    onEditPoi(poiId: string): void
}

const CategoriesPoisList = ({
    data,
    onEditCategory,
    onDragEnd,
    onDeleteCategory,
    onDeletePoi,
    onAddPoi,
    onEditPoi,
}: CategoriesPoisListProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    const [activeItem, setActiveItem] = React.useState<{ id: string; type: 'category' | 'poi' } | null>(null)

    const handleDragStart = (event: DragStartEvent) => {
        setActiveItem({
            id: String(event.active.id),
            type: event.active.data.current?.type ?? 'category',
        })
    }

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveItem(null)
        onDragEnd(event)
    }

    const handleDragCancel = () => {
        setActiveItem(null)
    }

    const activeCategory =
        activeItem?.type === 'category' ? data.find((c) => c.id === activeItem.id) ?? null : null
    const activePoi =
        activeItem?.type === 'poi'
            ? (data.flatMap((c) => c.pois ?? []).find((p) => p.id === activeItem.id) as ExtendedPoi | undefined) ??
            null
            : null

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext items={data.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {data.map((cat, index) => (
                    <CategoryLi
                        key={cat.id}
                        index={index}
                        data={cat}
                        onAddPoi={() => onAddPoi(cat.id)}
                        onEditCategory={onEditCategory}
                        onDeleteCategory={onDeleteCategory}
                    >
                        <SortableContext
                            items={(cat.pois ?? []).map((p) => p.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {cat.pois
                                ?.slice()
                                .sort(sortPoisByName())
                                .map((poi: ExtendedPoi) => (
                                    <PoiLi
                                        key={poi.id}
                                        data={poi}
                                        onEditPoi={onEditPoi}
                                        onDeletePoi={onDeletePoi}
                                    />
                                ))}
                        </SortableContext>
                    </CategoryLi>
                ))}
            </SortableContext>
            <DragOverlay>
                {activeCategory && <CategoryLiOverlay data={activeCategory} />}
                {activePoi && <PoiLiOverlay data={activePoi} />}
            </DragOverlay>
        </DndContext>
    )
}

export default CategoriesPoisList
